import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ToastService } from '@olv/ui-shared/ui-shared/toast/toast.service';
import { finalize, Subscription } from 'rxjs';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { DatePickerService, PresetPeriod, RangeOfDate } from '@olv/ui-shared/services/date-picker.service';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';
import { DatePickerComponent } from '@olv/ui-shared/ui-shared/date-picker/date-picker.component';
import { EmptyStateComponent } from '@olv/ui-shared/ui-shared/empty-state/empty-state.component';
import { LoadingSpinnerComponent } from '@olv/ui-shared/ui-shared/loading-spinner/loading-spinner.component';
import {
  CurrentUtilization,
  RESERVE_CATEGORY_LABELS,
  ReserveTapSourceOperationEnum,
  UTILIZATION_STATUS_LABELS,
  UtilizationReport,
  UtilizationReportRow,
  UtilizationStatusEnum,
} from './reports-utilization.models';
import { ReportsUtilizationService } from './reports-utilization.service';

@Component({
  selector: 'app-reports-utilization',
  standalone: true,
  imports: [
    CommonModule,
    HasPermissionDirective,
    ButtonComponent,
    LoadingSpinnerComponent,
    EmptyStateComponent,
    DatePickerComponent,
    MatIconModule,
  ],
  templateUrl: './reports-utilization.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './reports-utilization.component.scss',
})
export class ReportsUtilizationComponent implements OnInit {
  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
  protected readonly UtilizationStatusEnum = UtilizationStatusEnum;

  private readonly service = inject(ReportsUtilizationService);
  private readonly toastr = inject(ToastService);
  private readonly datePickerService = inject(DatePickerService);

  current: CurrentUtilization | null = null;
  isLoadingCurrent = false;
  currentFailed = false;

  report: UtilizationReport | null = null;
  rows: UtilizationReportRow[] = [];
  page = 1;
  readonly pageSize = 10;
  isLoadingReport = false;
  reportFailed = false;

  isExporting = false;

  fromDate!: string;
  toDate!: string;
  selectedPeriod = 'Month';

  ngOnInit(): void {
    this.setRange(this.datePickerService.presetPeriod('Month'));
    this.selectedPeriod = this.datePickerService.selectedPeriod;

    this.loadCurrent();
    this.loadReport();
  }

  loadCurrent(): void {
    this.isLoadingCurrent = true;
    this.currentFailed = false;

    this.service
      .getCurrent()
      .pipe(finalize(() => (this.isLoadingCurrent = false)))
      .subscribe({
        next: (result) => (this.current = result),
        error: (err) => {
          console.error('Error loading current utilization:', err);
          this.current = null;
          this.currentFailed = true;
        },
      });
  }

  private reportRequest?: Subscription;

  loadReport(): void {
    // A range picked while an older one is still loading supersedes it; cancelled first, so its finalize cannot
    // clear the flag the new request sets.
    this.reportRequest?.unsubscribe();
    this.isLoadingReport = true;
    this.reportFailed = false;

    this.reportRequest = this.service
      .getReport(this.fromDate, this.toDate)
      .pipe(finalize(() => (this.isLoadingReport = false)))
      .subscribe({
        next: (result) => {
          this.report = result;
          this.rows = [...(result?.rows ?? [])].sort((a, b) => b.date.localeCompare(a.date));
          this.page = 1;
        },
        error: (err) => {
          console.error('Error loading utilization report:', err);
          this.report = null;
          this.rows = [];
          this.reportFailed = true;
        },
      });
  }

  onPresetSelected(preset: string): void {
    this.setRange(this.datePickerService.presetPeriod(preset as PresetPeriod));
    this.selectedPeriod = this.datePickerService.selectedPeriod;
    this.loadReport();
  }

  onRangeSelected(range: RangeOfDate): void {
    this.setRange(this.datePickerService.dateRangeSelected(range));
    this.selectedPeriod = this.datePickerService.selectedPeriod;
    this.loadReport();
  }

  onExport(): void {
    if (this.isExporting) return;

    this.isExporting = true;
    this.service
      .exportReport(this.fromDate, this.toDate)
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: (blob) => this.downloadBlob(blob, this.exportFilename()),
        error: (err) => {
          console.error('Error exporting utilization report:', err);
          this.toastr.error('The utilization report could not be exported. Please try again.', 'Error');
        },
      });
  }

  formatInt(value: number | null): string {
    return value == null ? '—' : Math.round(value).toLocaleString('en-US');
  }

  formatPercent(value: number | null): string {
    return value == null ? '—' : `${value.toFixed(1)}%`;
  }

  progressWidth(value: number | null): number {
    return value == null ? 0 : Math.max(0, Math.min(100, value));
  }

  statusLabel(status: UtilizationStatusEnum): string {
    return UTILIZATION_STATUS_LABELS[status];
  }

  statusClass(status: UtilizationStatusEnum): string {
    switch (status) {
      case UtilizationStatusEnum.Over:
        return 'over';
      case UtilizationStatusEnum.Under:
        return 'under';
      case UtilizationStatusEnum.Within:
        return 'within';
      default:
        return 'not-assessed';
    }
  }

  categoryLabel(category: ReserveTapSourceOperationEnum): string {
    return RESERVE_CATEGORY_LABELS[category];
  }

  get hasReserveConsumption(): boolean {
    return (this.current?.reserveConsumptionByCategory?.length ?? 0) > 0;
  }

  get hasReportRows(): boolean {
    return this.rows.length > 0;
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.rows.length / this.pageSize));
  }

  get pageRows(): UtilizationReportRow[] {
    const start = (this.page - 1) * this.pageSize;
    return this.rows.slice(start, start + this.pageSize);
  }

  get pageRangeLabel(): string {
    const first = (this.page - 1) * this.pageSize + 1;
    const last = Math.min(this.page * this.pageSize, this.rows.length);
    return `${first}–${last} of ${this.rows.length}`;
  }

  previousPage(): void {
    if (this.page > 1) this.page--;
  }

  nextPage(): void {
    if (this.page < this.pageCount) this.page++;
  }

  private exportFilename(): string {
    return `utilization-report-${this.fromDate.replace(/-/g, '')}-${this.toDate.replace(/-/g, '')}.xlsx`;
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    // Deferred: Safari on iOS/iPadOS can abort the download when the URL is revoked in the same task as the click.
    setTimeout(() => URL.revokeObjectURL(url));
  }

  private setRange(range: RangeOfDate): void {
    this.fromDate = this.toYmd(range.start);
    this.toDate = this.toYmd(range.end);
  }

  // Forces the date's own Y/M/D into UTC before formatting, so a local-midnight Date from the picker
  // never shifts a day under toISOString().
  private toYmd(date: Date): string {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    return d.toISOString().slice(0, 10);
  }
}
